import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { BasketVariationViewModel } from '../models/BasketVariationViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { GetLoyaltyModel } from '../models/GetLoyaltyModel';
import type { SeturLoyaltyCheckCustomerRequestViewModel } from '../models/SeturLoyaltyCheckCustomerRequestViewModel';
import type { SeturLoyaltyClubRegisterRequestViewModel } from '../models/SeturLoyaltyClubRegisterRequestViewModel';
import type { SeturLoyaltyClubRegisterViewModel } from '../models/SeturLoyaltyClubRegisterViewModel';
import type { SeturLoyaltyConfigItemViewModel } from '../models/SeturLoyaltyConfigItemViewModel';
import type { SeturLoyaltyCustomerClubSegmentsViewModel } from '../models/SeturLoyaltyCustomerClubSegmentsViewModel';
import type { SeturLoyaltyCustomerCouponsViewModel } from '../models/SeturLoyaltyCustomerCouponsViewModel';
import type { SeturLoyaltyPointsHistoryViewModel } from '../models/SeturLoyaltyPointsHistoryViewModel';
import type { SeturLoyaltyPointsViewModel } from '../models/SeturLoyaltyPointsViewModel';
import type { SeturLoyaltySignUpRequestViewModel } from '../models/SeturLoyaltySignUpRequestViewModel';
import type { SeturLoyaltySignUpViewModel } from '../models/SeturLoyaltySignUpViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class LoyaltiesService {
  /**
   * Validate KocAilem
   * Validates loyalty for koc ailem by number and type (TCKN, KARTNO, ALTINKOD)
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateKocAilem(
    {
      number,
      type,
      basketId,
    }: {
      number?: string;
      type?: any;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/v2/loyalties/validate/kocailem`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Zubizu
   * Validates loyalty for zubizu by code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateZubizu(
    {
      code,
      basketId,
    }: {
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/v2/loyalties/validate/zubizu`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Hopi
   * Validates loyalty for hopi by item and type (CARDNO, TOKEN)
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateHopi(
    {
      item,
      type,
      basketId,
    }: {
      item?: string;
      type?: any;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/v2/loyalties/validate/hopi`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(item ? { item: item } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate TurkTelekom
   * Validates loyalty for turk telekom by number and code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateTurkTelekom(
    {
      number,
      code,
      basketId,
    }: {
      number?: string;
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/v2/loyalties/validate/turktelekom`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate TurkTelekom Prime
   * Validates loyalty for turk telekom prime by number and code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateTurkTelekomPrime(
    {
      number,
      code,
      basketId,
    }: {
      number?: string;
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/v2/loyalties/validate/turktelekomprime`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate TurkTelekom Business Prime
   * Validates loyalty for turk telekom business prime by number and code
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateTurkTelekomBusinessPrime(
    {
      number,
      code,
      basketId,
    }: {
      number?: string;
      code?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/v2/loyalties/validate/turktelekombusinessprime`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Paro
   * Validates loyalty for paro by number
   * @returns GetLoyaltyModel Success
   * @throws ApiError
   */
  public static async validateParo(
    {
      number,
      basketId,
    }: {
      number?: string;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetLoyaltyModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetLoyaltyModel>({
      method: 'GET',
      url: `/v2/loyalties/validate/paro`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(number ? { number: number } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get Hopi Variation
   * Gets hopi variations by item type
   * @returns BasketVariationViewModel Success
   * @throws ApiError
   */
  public static async getHopiVariations(
    {
      basketId,
      itemId,
      type,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      itemId?: string;
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<BasketVariationViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<BasketVariationViewModel>>({
      method: 'GET',
      url: `/v2/loyalties/variations/hopi`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(itemId ? { itemId: itemId } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Switch Variation
   * Switches current variation to another
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async switch(
    {
      basketId,
      itemId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      itemId?: string;
      body?: BasketVariationViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/loyalties/variations/switch`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(itemId ? { itemId: itemId } : {}),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Check Customer
   * Checks customer for setur loyalty
   * @returns string Success
   * @throws ApiError
   */
  public static async check(
    {
      body,
    }: {
      body: SeturLoyaltyCheckCustomerRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/v2/loyalties/setur/check`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Points History
   * Gets points history by identifiers
   * @returns SeturLoyaltyPointsHistoryViewModel Success
   * @throws ApiError
   */
  public static async pointsHistory(
    {
      authorization,
      sourceSystems,
      pointTypes,
      masterId,
      startDate,
      endDate,
      dateRange,
      blocked,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      sourceSystems?: Array<any>;
      pointTypes?: Array<any>;
      masterId?: string;
      startDate?: string;
      endDate?: string;
      /**
       * Filter: Last3Months, Last6Months or Last12Months
       */
      dateRange?: any;
      blocked?: boolean;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturLoyaltyPointsHistoryViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SeturLoyaltyPointsHistoryViewModel>({
      method: 'GET',
      url: `/v2/loyalties/setur/points-history`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(sourceSystems ? { sourceSystems: sourceSystems } : {}),
        ...(pointTypes ? { pointTypes: pointTypes } : {}),
        ...(masterId ? { masterId: masterId } : {}),
        ...(startDate ? { startDate: startDate } : {}),
        ...(endDate ? { endDate: endDate } : {}),
        ...(dateRange ? { dateRange: dateRange } : {}),
        ...(blocked ? { blocked: blocked } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Sign Up
   * Signs up customer to loyalty program
   * @returns SeturLoyaltySignUpViewModel Success
   * @throws ApiError
   */
  public static async loyaltySignUp(
    {
      body,
    }: {
      body?: SeturLoyaltySignUpRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturLoyaltySignUpViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SeturLoyaltySignUpViewModel>({
      method: 'POST',
      url: `/v2/loyalties/setur/sign-up`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Clubs
   * Gets customer clubs
   * @returns SeturLoyaltyCustomerClubSegmentsViewModel Success
   * @throws ApiError
   */
  public static async getClubs(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturLoyaltyCustomerClubSegmentsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SeturLoyaltyCustomerClubSegmentsViewModel>({
      method: 'GET',
      url: `/v2/loyalties/setur/clubs`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Clubs
   * Adds customer to loyalty clubs
   * @returns SeturLoyaltyClubRegisterViewModel Success
   * @throws ApiError
   */
  public static async joinClubs(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: SeturLoyaltyClubRegisterRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturLoyaltyClubRegisterViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SeturLoyaltyClubRegisterViewModel>({
      method: 'POST',
      url: `/v2/loyalties/setur/clubs`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Points
   * Gets total points by identifiers
   * @returns SeturLoyaltyPointsViewModel Success
   * @throws ApiError
   */
  public static async pointsHistoryV2(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturLoyaltyPointsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SeturLoyaltyPointsViewModel>({
      method: 'GET',
      url: `/v2/loyalties/setur/points`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Coupons
   * Gets coupons by identifiers
   * @returns SeturLoyaltyCustomerCouponsViewModel Success
   * @throws ApiError
   */
  public static async getCoupons(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturLoyaltyCustomerCouponsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SeturLoyaltyCustomerCouponsViewModel>({
      method: 'GET',
      url: `/v2/loyalties/setur/coupons`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Configs
   * Gets configs for setur loyalty
   * @returns SeturLoyaltyConfigItemViewModel Success
   * @throws ApiError
   */
  public static async getConfigs(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<SeturLoyaltyConfigItemViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<SeturLoyaltyConfigItemViewModel>>({
      method: 'GET',
      url: `/v2/loyalties/setur/configs`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Clubs Validate
   * Validates customer for loyalty clubs
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async validateClubs(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: SeturLoyaltyClubRegisterRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/loyalties/setur/clubs/validate`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v2' {
  interface Services {
    LoyaltiesService: typeof LoyaltiesService;
  }
}
